import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useEventTracking } from '@services/statsig';
import type { WaitingRoomQueueEntryResponse } from '@store/generated/waiting-room';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { useTelehealthAccess } from '../../hooks/useTelehealthAccess';
import {
  selectWaitingRoomEntries,
  selectWaitingRoomError,
  selectWaitingRoomLink,
  selectWaitingRoomProviderAvailability,
  selectWaitingRoomSseConnectionState,
  useAdmitWaitingRoomPatientMutation,
  useGetProviderWaitingRoomLinkQuery,
  useGetProviderWaitingRoomQueueQuery,
  useNotifyWaitingRoomPatientMutation,
  useProviderWaitingRoomHeartbeatMutation,
  useRemoveWaitingRoomPatientMutation,
  useSetProviderWaitingRoomAvailabilityMutation,
  useSubscribeProviderWaitingRoomQueueQuery,
  waitingRoomActions,
} from '../store';
import type {
  ProviderWaitingRoomAdmitResponse,
  ProviderWaitingRoomNotifyRequest,
  ProviderWaitingRoomNotifyResponse,
  ProviderWaitingRoomRemoveRequest,
  ProviderWaitingRoomRemoveResponse,
  ProviderWaitingRoomRuntime,
  WaitingRoomAvailabilityTelemetryOptions,
  WaitingRoomEntry,
  WaitingRoomProviderAvailability,
  WaitingRoomQueueState,
} from '../types';

const HEARTBEAT_INTERVAL_MS = 60_000;

const normalizeEntry = (entry: WaitingRoomQueueEntryResponse): WaitingRoomEntry => ({
  ...entry,
  current_state: entry.current_state as WaitingRoomQueueState,
});

const isProviderNotOnlineError = (error: unknown) => {
  const apiError = error as
    | {
        status?: number;
        data?: { error_code?: string };
      }
    | undefined;

  return apiError?.status === 409 && apiError.data?.error_code === 'provider_not_online';
};

export function useProviderWaitingRoomRuntime(): ProviderWaitingRoomRuntime {
  const dispatch = useAppDispatch();
  const { telehealth } = useEventTracking();
  const { hasAccess, isLoading: isAccessLoading } = useTelehealthAccess();
  const isEnabled = hasAccess && !isAccessLoading;

  const entries = useAppSelector(selectWaitingRoomEntries);
  const link = useAppSelector(selectWaitingRoomLink);
  const providerAvailability = useAppSelector(selectWaitingRoomProviderAvailability);
  const sseConnectionState = useAppSelector(selectWaitingRoomSseConnectionState);
  const error = useAppSelector(selectWaitingRoomError);

  const { data: linkData, isLoading: isLinkLoading } = useGetProviderWaitingRoomLinkQuery(
    undefined,
    {
      skip: !isEnabled,
    },
  );

  const {
    data: queueData,
    isLoading: isQueueLoading,
    refetch: refetchQueue,
  } = useGetProviderWaitingRoomQueueQuery(undefined, {
    skip: !isEnabled,
  });

  useSubscribeProviderWaitingRoomQueueQuery(undefined, { skip: !isEnabled });

  const [setAvailabilityMutation, setAvailabilityState] =
    useSetProviderWaitingRoomAvailabilityMutation();
  const [heartbeat] = useProviderWaitingRoomHeartbeatMutation();
  const [admitPatientMutation, admitPatientState] = useAdmitWaitingRoomPatientMutation();
  const [notifyPatientMutation, notifyPatientState] = useNotifyWaitingRoomPatientMutation();
  const [removeQueueEntryMutation, removeQueueEntryState] = useRemoveWaitingRoomPatientMutation();

  const providerAvailabilityRef = useRef(providerAvailability);
  const hasAutoSetOnlineRef = useRef(false);
  const providerOptedOutRef = useRef(false);

  useEffect(() => {
    providerAvailabilityRef.current = providerAvailability;
  }, [providerAvailability]);

  useEffect(() => {
    if (linkData) {
      dispatch(waitingRoomActions.linkReceived(linkData));
    }
  }, [dispatch, linkData]);

  useEffect(() => {
    if (!queueData) {
      return;
    }

    dispatch(
      waitingRoomActions.queueSnapshotReceived({
        type: 'queue_snapshot',
        entries: (queueData.entries ?? []).map(normalizeEntry),
        provider_status: queueData.provider_status,
      }),
    );
  }, [dispatch, queueData]);

  useEffect(() => {
    if (!isEnabled || providerAvailability !== 'online') {
      return;
    }

    const heartbeatProvider = async () => {
      try {
        const response = await heartbeat().unwrap();
        dispatch(waitingRoomActions.availabilityChanged(response.status));
      } catch (heartbeatError) {
        if (isProviderNotOnlineError(heartbeatError)) {
          dispatch(waitingRoomActions.availabilityChanged('offline'));
          return;
        }
        dispatch(waitingRoomActions.waitingRoomErrorReceived('Heartbeat failed.'));
      }
    };

    const intervalId = window.setInterval(() => {
      void heartbeatProvider();
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [dispatch, heartbeat, isEnabled, providerAvailability]);

  useEffect(() => {
    return () => {
      if (providerAvailabilityRef.current === 'online') {
        void setAvailabilityMutation({
          waitingRoomAvailabilityRequest: { status: 'offline' },
        });
      }
    };
  }, [setAvailabilityMutation]);

  const setAvailability = useCallback(
    async (
      status: WaitingRoomProviderAvailability,
      options?: WaitingRoomAvailabilityTelemetryOptions,
    ) => {
      providerOptedOutRef.current = status === 'offline';
      const previousStatus = providerAvailabilityRef.current;
      const source = options?.source ?? 'runtime';

      try {
        const response = await setAvailabilityMutation({
          waitingRoomAvailabilityRequest: { status },
        }).unwrap();
        dispatch(waitingRoomActions.availabilityChanged(response.status));
        telehealth.waitingRoomAvailabilityChanged({
          source,
          previous_status: previousStatus,
          requested_status: status,
          status: response.status,
          outcome: 'success',
        });
      } catch (error) {
        const errorCode = error instanceof Error ? error.name : 'unknown_error';
        telehealth.waitingRoomAvailabilityChanged({
          source,
          previous_status: previousStatus,
          requested_status: status,
          outcome: 'failed',
          error_code: errorCode,
        });
        throw error;
      }
    },
    [dispatch, setAvailabilityMutation, telehealth],
  );

  useEffect(() => {
    if (
      !isEnabled ||
      providerAvailability !== 'offline' ||
      providerOptedOutRef.current ||
      hasAutoSetOnlineRef.current
    ) {
      return;
    }

    hasAutoSetOnlineRef.current = true;
    void setAvailability('online', { source: 'auto_online' }).catch(() => {
      hasAutoSetOnlineRef.current = false;
      dispatch(waitingRoomActions.waitingRoomErrorReceived('Failed to set waiting room online.'));
    });
  }, [dispatch, isEnabled, providerAvailability, setAvailability]);

  const refreshQueue = useCallback(async () => {
    await refetchQueue();
  }, [refetchQueue]);

  const admitPatient = useCallback(
    async (
      entryId: string,
      encounterId: string,
      additionalParticipantEntryIds: string[] = [],
    ): Promise<ProviderWaitingRoomAdmitResponse> => {
      const response = await admitPatientMutation({
        entryId,
        waitingRoomAdmitRequest: {
          encounter_id: encounterId,
          additional_participant_entry_ids: additionalParticipantEntryIds,
        },
      }).unwrap();
      return response;
    },
    [admitPatientMutation],
  );

  const removeQueueEntry = useCallback(
    async (
      entryId: string,
      request: ProviderWaitingRoomRemoveRequest,
    ): Promise<ProviderWaitingRoomRemoveResponse> => {
      const response = await removeQueueEntryMutation({
        entryId,
        waitingRoomRemoveRequest: request,
      }).unwrap();
      return response;
    },
    [removeQueueEntryMutation],
  );

  const notifyPatient = useCallback(
    async (
      request: ProviderWaitingRoomNotifyRequest,
    ): Promise<ProviderWaitingRoomNotifyResponse> => {
      const response = await notifyPatientMutation({
        waitingRoomNotifyPatientRequest: request,
      }).unwrap();
      return response;
    },
    [notifyPatientMutation],
  );

  return useMemo(
    () => ({
      hasAccess,
      isLoading: isAccessLoading || isLinkLoading || isQueueLoading,
      link,
      entries,
      providerAvailability,
      sseConnectionState,
      error,
      isSettingAvailability: setAvailabilityState.isLoading,
      isAdmitting: admitPatientState.isLoading,
      isSendingInvite: notifyPatientState.isLoading,
      isRemoving: removeQueueEntryState.isLoading,
      setAvailability,
      refreshQueue,
      admitPatient,
      removeQueueEntry,
      notifyPatient,
    }),
    [
      admitPatient,
      admitPatientState.isLoading,
      entries,
      error,
      hasAccess,
      isAccessLoading,
      isLinkLoading,
      isQueueLoading,
      link,
      notifyPatient,
      notifyPatientState.isLoading,
      providerAvailability,
      refreshQueue,
      removeQueueEntry,
      removeQueueEntryState.isLoading,
      setAvailability,
      setAvailabilityState.isLoading,
      sseConnectionState,
    ],
  );
}
